import '@core/extensions/string.extensions';
import 'zone.js';

import { DestroyRef, enableProdMode, NgModuleRef } from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { platformBrowser } from '@angular/platform-browser';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { Router } from '@angular/router';
import { resolveMonacoVsBase, setMonacoVsBase } from '@core/utils/monaco-embed';
import { environment } from '@env';
import { bindHostRouteToRouter, type PlatformAPI, type RemoteMountProps } from '@ol/kit';

import { AppModule } from './app/app.module';
import { AppEmbedModule } from './app/embed/app-embed.module';
import { DesignerEmbedRoot } from './app/embed/designer-embed-root.component';

const TAG = 'designer-root';
const PROP_KEYS: ReadonlyArray<keyof RemoteMountProps> = [
  'accessToken',
  'user',
  'route',
  'featureFlags',
];

if (environment.production) {
  enableProdMode();
}

// Monaco's assets live on THIS remote's origin, not the host document's —
// resolve them against the bundle URL so the AMD loader (and the workers it
// derives from the same base) still load when the Designer is
// federation-embedded in the Hub (see monaco-embed.ts). No MonacoEnvironment
// setup HERE: in standalone mode monaco ≥0.53's min build installs its own
// getWorker (overwriting any assignment), and in bundled/embed mode the
// monacoRequire shim installs the CSP-safe one lazily.
setMonacoVsBase(resolveMonacoVsBase(import.meta.url));

// Monaco's AMD loader rejects (and sometimes re-throws in a timer) an in-flight
// `editor.main` module load with a CancellationError ("Canceled") when an editor
// is disposed before loading finishes — e.g. navigating away from a view that
// hosts the JSON editor. The editor being torn down is the one going away, so it
// is benign but noisy. Suppress ONLY that specific cancellation, surfaced either
// as an unhandled rejection or an uncaught error; every other failure is left to
// surface normally so real errors are never masked.
const isMonacoCancellation = (reason: unknown): boolean => {
  if (reason === 'Canceled') {
    return true;
  }
  if (!reason || typeof reason !== 'object') {
    return false;
  }
  const { name, message } = reason as { name?: string; message?: string };
  return name === 'Canceled' || name === 'CancellationError' || message === 'Canceled';
};

window.addEventListener('unhandledrejection', (event) => {
  if (isMonacoCancellation(event.reason)) {
    event.preventDefault();
  }
});

window.addEventListener('error', (event) => {
  if (isMonacoCancellation(event.error) || isMonacoCancellation(event.message)) {
    event.preventDefault();
  }
});

// Embedded when either: (a) Hub already installed __platform__ on window
// (its app initializer fires before route activation), OR (b) Hub set the
// synchronous __platformHost__ flag at the top of its own main.ts — this
// covers the window between Hub bootstrap and the platform install where
// loadRemoteModule would otherwise mistake us for standalone.
const win = window as unknown as { __platform__?: PlatformAPI; __platformHost__?: true };
const isEmbedded = !!win.__platform__ || win.__platformHost__ === true;

// When embedded, the host page does not include Designer's global styles
// (Hub only import()s the JS module — index.html is not loaded). Pull the
// stylesheet <link>s from our own index.html and inject them into the host
// document, preserving hashed filenames.
const STYLES_MARK = 'data-designer-styles';

async function injectRemoteStyles(): Promise<void> {
  if (document.querySelector(`link[${STYLES_MARK}]`)) {
    console.log('[designer-embed] styles already injected, skipping');
    return;
  }
  try {
    const indexUrl = new URL('index.html', import.meta.url).href;
    console.log('[designer-embed] fetching index.html for styles', indexUrl);
    const html = await fetch(indexUrl, { credentials: 'omit' }).then((r) => r.text());
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const links = Array.from(doc.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'));
    for (const source of links) {
      const href = new URL(source.getAttribute('href') ?? '', indexUrl).href;
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.setAttribute(STYLES_MARK, '');
      document.head.appendChild(link);
    }
  } catch (err) {
    console.error('[designer-embed] failed to inject remote styles', err);
  }
}

// Static <img src="../../../assets/foo.svg"> in templates resolves against
// the host document URL (Platform), not this remote's bundle, so every such
// image 404s against the host root. Rebase them onto this bundle instead.
//
// Everything here is deliberately scoped to what this remote owns. Behind the
// on-prem gateway the shell serves its OWN /assets/** from the same origin, so
// a document-wide rewrite would repoint the shell's images at /ol-integration/
// and break them.
function startAssetRewrites(container: HTMLElement): () => void {
  // Compare against the bundle's base URL, not its origin: behind the platform
  // gateway the host and this remote share an origin and differ only by path
  // prefix (/ol-integration/), so an origin check would find nothing to fix and
  // let every asset 404 against the host root.
  const remoteBase = new URL('./', import.meta.url).href;
  const hostOrigin = window.location.origin;
  if (remoteBase === `${hostOrigin}/`) return () => undefined;

  // Angular's esbuild output puts verbatim assets at /assets/ and
  // bundler-processed (hashed) ones at /media/. Both live under the remote's
  // base, not at the host root.
  const isRemoteAssetPath = (pathname: string): boolean => /^\/(assets|media)\//.test(pathname);
  const rebase = (url: URL): string => `${remoteBase}${url.pathname.slice(1)}${url.search}`;

  const fix = (img: HTMLImageElement): void => {
    const raw = img.getAttribute('src');
    if (!raw) return;
    if (raw.startsWith(remoteBase)) return;
    let url: URL;
    try {
      url = new URL(raw, document.baseURI);
    } catch {
      return;
    }
    if (url.href.startsWith(remoteBase)) return;
    if (url.origin !== hostOrigin) return;
    if (!isRemoteAssetPath(url.pathname)) return;
    img.src = rebase(url);
  };

  container.querySelectorAll<HTMLImageElement>('img[src]').forEach(fix);

  const imgObserver = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === 'attributes' && m.target instanceof HTMLImageElement) {
        fix(m.target);
      }
      m.addedNodes.forEach((node) => {
        if (node instanceof HTMLImageElement) fix(node);
        else if (node instanceof Element) {
          node.querySelectorAll<HTMLImageElement>('img').forEach(fix);
        }
      });
    }
  });
  imgObserver.observe(container, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['src'],
  });

  // Overlays (ng-bootstrap modals, CDK panels) are portaled to document.body,
  // outside the container. A 404 is the ownership test no DOM boundary can
  // give us: a host image resolving correctly never fires this.
  const retried = new WeakSet<HTMLImageElement>();
  const onImgError = (e: Event): void => {
    const tgt = e.target;
    if (!(tgt instanceof HTMLImageElement)) return;
    if (retried.has(tgt)) return;
    retried.add(tgt);
    fix(tgt);
  };
  document.addEventListener('error', onImgError, true);

  // CSS URLs (background-image, mask-image, …) inside Angular component <style>
  // blocks have the same problem and no error event to key off. Every block
  // already in the document belongs to the host, so mark those handled and
  // rewrite only what Angular injects from here on.
  const STYLE_HANDLED = '__designerAssetRewritten';
  // Match any url(...) reference. We decide what to rewrite inside the
  // replacement function using the URL parser, which correctly handles
  // any number of '../', absolute paths, and bare paths alike.
  const cssUrlRe = /url\(\s*(['"]?)([^'")\s]+)\1\s*\)/g;
  const markHandled = (style: HTMLStyleElement): void => {
    (style as unknown as Record<string, unknown>)[STYLE_HANDLED] = true;
  };
  const fixStyle = (style: HTMLStyleElement): void => {
    const tagged = style as unknown as Record<string, unknown>;
    if (tagged[STYLE_HANDLED]) return;
    const css = style.textContent;
    if (!css) {
      markHandled(style);
      return;
    }
    const rewritten = css.replace(cssUrlRe, (full, quote, path) => {
      if (/^(data:|https?:|\/\/|blob:)/i.test(path)) return full;
      let resolved: URL;
      try {
        resolved = new URL(path, document.baseURI);
      } catch {
        return full;
      }
      if (resolved.href.startsWith(remoteBase)) return full;
      if (!isRemoteAssetPath(resolved.pathname)) return full;
      return `url(${quote}${rebase(resolved)}${quote})`;
    });
    if (rewritten !== css) {
      style.textContent = rewritten;
    }
    markHandled(style);
  };
  document.querySelectorAll<HTMLStyleElement>('style').forEach(markHandled);
  const styleObserver = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach((node) => {
        if (node instanceof HTMLStyleElement) fixStyle(node);
        else if (node instanceof Element) {
          node.querySelectorAll<HTMLStyleElement>('style').forEach(fixStyle);
        }
      });
    }
  });
  styleObserver.observe(document.head, { childList: true, subtree: true });

  return () => {
    imgObserver.disconnect();
    styleObserver.disconnect();
    document.removeEventListener('error', onImgError, true);
  };
}

if (isEmbedded) {
  void injectRemoteStyles();
}

// Embedded path: Hub loads this module via Native Federation. Do NOT
// auto-bootstrap; expose mount/unmount for the host wrapper to call.
let embedPromise: Promise<NgModuleRef<AppEmbedModule>> | null = null;

const ensureEmbedded = (): Promise<NgModuleRef<AppEmbedModule>> => {
  if (!embedPromise) {
    console.log('[designer-embed] bootstrapping AppEmbedModule');
    embedPromise = platformBrowserDynamic()
      .bootstrapModule(AppEmbedModule)
      .then((moduleRef) => {
        console.log('[designer-embed] AppEmbedModule bootstrapped');
        if (!customElements.get(TAG)) {
          customElements.define(
            TAG,
            createCustomElement(DesignerEmbedRoot, { injector: moduleRef.injector }),
          );
        }
        return moduleRef;
      })
      .catch((err: unknown) => {
        console.error('[designer-embed] AppEmbedModule bootstrap FAILED', err);
        throw err;
      });
  }
  return embedPromise;
};

const setProps = (el: HTMLElement, props: RemoteMountProps): void => {
  const target = el as unknown as Record<string, unknown>;
  for (const k of PROP_KEYS) target[k] = props[k];
};

// Armed for the lifetime of a mount only, so the observers cannot outlive the
// remote and start rewriting the shell's own DOM once it is back on screen.
let stopAssetRewrites: (() => void) | null = null;

export async function mount(container: HTMLElement, props: RemoteMountProps): Promise<void> {
  stopAssetRewrites?.();
  // Before ensureEmbedded(), so the <style> blocks Angular injects while
  // bootstrapping are already being watched.
  stopAssetRewrites = startAssetRewrites(container);
  const moduleRef = await ensureEmbedded();
  let node = container.querySelector<HTMLElement>(TAG);
  if (!node) {
    node = document.createElement(TAG);
    container.appendChild(node);
    // Before setProps, so the shell's first route arrives through the accessor.
    // This is the only channel that carries browser back/forward: the embedded
    // router has no popstate listener of its own, because Angular Elements
    // never calls ApplicationRef.bootstrap().
    bindHostRouteToRouter(
      node,
      moduleRef.injector.get(Router),
      moduleRef.injector.get(DestroyRef),
    );
  }
  setProps(node, props);
}

export function unmount(container: HTMLElement): void {
  stopAssetRewrites?.();
  stopAssetRewrites = null;
  container.querySelector(TAG)?.remove();
}

// Standalone path: when the file is loaded directly via index.html (no host
// platform present), boot the full AppModule as the page.
if (!isEmbedded) {
  console.log('[designer-embed] standalone path — bootstrapping AppModule');
  platformBrowser()
    .bootstrapModule(AppModule)
    .catch((err: unknown) => console.error('[designer-embed] standalone bootstrap failed', err));
}
